import { Component, HostBinding, ViewEncapsulation, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription, Observable, debounceTime, distinctUntilChanged, map } from 'rxjs';
import { FieldExpression, LuceneExpression, NodeExpression, term,
	parse as luceneToParse, toString as luceneToString } from 'lucene';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Location, NgIf, NgFor } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { CURRENCY_OPTIONS, UNIT_OPTIONS } from '../../../shared/models/list';
import {
	QualityIssue,
	ClaimStatus,
	PENDING_STATUS_LIST,
	CLOSED_STATUS_LIST,
	DECISION_TR,
	STATUS_TR,
	ISSUE_TYPE_TR
} from '../../models/quality-issue';
import { QualityIssueService, ISSUE_FIELDS } from '../../services/quality-issue.service';
import { UserService } from '../../../shared/services/user.service';
import { User } from '../../../shared/models/user';
import { OrderByDirection, OrderBy, UNDERSCORE } from '../../../shared/utils/constant';
import { ListFilter } from '../../../shared/models/filter';
import { ListFilterService } from '../../../shared/services/list-filter.service';
import { DatacenterType } from '../../../shared/models/datacenter-aggrid-view';
import { State } from '../../../order/models/localization-state';
import { AllQualityIssueService } from '../../services/all-quality-issue.service';
import { AllQualityIssue, ALL_ISSUE_FIELDS } from '../../models/all-quality-issue';
import { QualityIssueStatus } from '../../models/all-quality-issue-constants';
import { DatacenterBooleanFilterType } from '../../../shared/models/datacenter-filters';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';

@Component({
	selector: 'copo-claim-dashboard',
	templateUrl: './claim-list.component.html',
	styleUrl: './claim-list.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		BackButtonComponent,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule,
		NgFor,
		VirtualScrollerDirective,
		RouterLink,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe
	]
})
export class ClaimListComponent implements OnDestroy {
	@HostBinding('class.passthrough') componentClass = true;

	readonly UNIT_OPTIONS = UNIT_OPTIONS;
	readonly UNDERSCORE = UNDERSCORE;

	user: User;

	searchTextSubscription: Subscription;
	globalSearchTextSubscription?: Subscription;
	searchFocus = false;
	searchTextControl = new FormControl('', { nonNullable: true });
	filters = '';
	currentFilters?: ListFilter;
	datacenterType: DatacenterType;
	mergedFilter?: LuceneExpression;
	state: State;
	pendingNotPushFilter = false;
	notPushedQualityIssues: AllQualityIssue[] = [];
	notPushedQualityIssuesSubscription?: Subscription;

	constructor(
		private readonly userService: UserService,
		private readonly qualityIssueService: QualityIssueService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly listFilterService: ListFilterService,
		private readonly allQualityIssueService: AllQualityIssueService,
		location: Location
	) {
		this.datacenterType = <DatacenterType> this.route.snapshot.data['type'];
		this.state = <State> location.getState();
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.setFilters(value);
		});
		if (this.router.isActive(
			'search',
			{ paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' }
		)) {
			this.searchTextControl.setValue(<string | undefined> this.route.snapshot.params['query'] ?? '');
			this.globalSearchTextSubscription = this.route.params.subscribe({
				next: params => {
					this.searchTextControl.setValue(<string | undefined> params['query'] ?? '');
				},
				error: (err: unknown) => {
					console.error(err);
				}
			});
		}

		this.currentFilters = this.getAppliedFilters();
		const statefilter = this.state.filter || this.currentFilters.filterState;
		if (statefilter) {
			if (statefilter.length === 0) {
				this.pendingNotPushFilter = true;
			} else {
				const filterType = statefilter.find(type => type.id === 'status');
				if (filterType) {
					const valueFilter = <DatacenterBooleanFilterType[]>filterType.values[0];
					const statusList = new Set(['claimstatus.pending', 'claimstatus.created']);
					const valList = valueFilter.filter(val => val.selected && val.value && statusList.has(val.value));
					if (valList.length > 0) {
						this.pendingNotPushFilter = true;
					}
				}
			}
		}

		if (this.pendingNotPushFilter) {
			this.notPushedQualityIssuesSubscription = this.allQualityIssueService.getQualityIssues(
				ALL_ISSUE_FIELDS,
				this.allQualityIssueService.getAllIssuesbyPendingNotPushedStatusFilter(),
				undefined,
				50
			)
				.subscribe(issues => {
					this.notPushedQualityIssues = issues.results;
				});
		}

		let criterionFilterNode;
		if (this.listFilterService.getLuceneFromAllFilters(this.currentFilters)) {
			criterionFilterNode = this.listFilterService.getLuceneFromAllFilters(this.currentFilters);
		}
		if (criterionFilterNode !== undefined) {
			this.mergedFilter = criterionFilterNode;
		}
		this.setFilters(this.searchTextControl.value);
		this.user = this.userService.user;
	}

	get dataId() {
		return `claim-${ this.filters }`;
	}

	ngOnDestroy() {
		this.searchTextSubscription.unsubscribe();
		this.globalSearchTextSubscription?.unsubscribe();
		if (this.notPushedQualityIssuesSubscription) {
			this.notPushedQualityIssuesSubscription.unsubscribe();
		}
	}

	isDraft(claim: QualityIssue) {
		return claim.status === ClaimStatus.DRAFT;
	}

	isPending(claim: QualityIssue) {
		return claim.status !== undefined ? PENDING_STATUS_LIST.includes(claim.status) : false;
	}

	isPendingNotPush(claim: AllQualityIssue) {
		return QualityIssueStatus.PENDING_NOT_PUSH === claim.statusTr;
	}

	isPaid(claim: QualityIssue) {
		return claim.status !== undefined && claim.status === ClaimStatus.PAID;
	}

	isRefused(claim: QualityIssue) {
		return claim.status !== undefined && claim.status === ClaimStatus.REFUSED;
	}

	isClosed(claim: QualityIssue) {
		return claim.status !== undefined ? CLOSED_STATUS_LIST.includes(claim.status) : false;
	}

	isUnknown(claim: QualityIssue) {
		return claim.status === ClaimStatus.UNKNOWN;
	}

	isOther(issue: QualityIssue) {
		return !this.isPending(issue) && !this.isPaid(issue) && !this.isRefused(issue);
	}

	getUnitAmount(amountUnitTr: string) {
		return CURRENCY_OPTIONS[<keyof typeof CURRENCY_OPTIONS> amountUnitTr.toLowerCase()];
	}

	getIssueStatusTr(issue: QualityIssue) {
		if (issue.status !== undefined) {
			return this.isClosed(issue) ? DECISION_TR[issue.status] : STATUS_TR[issue.status];
		}
		return 'quapp.error';
	}

	getDecisionTr(issue: QualityIssue) {
		return issue.status !== undefined ? DECISION_TR[issue.status] : 'quapp.error';
	}

	getIssueTypeTr(issue: QualityIssue) {
		return issue.issueType === 'issue' ? ISSUE_TYPE_TR.claim : ISSUE_TYPE_TR.warning;
	}

	getIssueType(issue: AllQualityIssue) {
		return issue.type === 'issue' ? ISSUE_TYPE_TR.claim : ISSUE_TYPE_TR.warning;
	}

	getBatchNumberList(issue: QualityIssue, underscore?: boolean) {
		if (issue.batches.length > 0) {
			return issue.batches.map(batch => batch.batchReference).join(', ');
		}
		return underscore ? UNDERSCORE : '';
	}

	getBatchNumbers(issue: AllQualityIssue, underscore?: boolean) {
		if (issue.batches.length > 0) {
			return issue.batches.map(batch => batch.reference).join(', ');
		}
		return underscore ? UNDERSCORE : '';
	}

	setFilters(fullTextStr: string) {
		this.filters = fullTextStr !== ''
			? this.mergedFilter
				? this.createQuickSearchAndFilterQuery(this.createQuickSearchFilter(fullTextStr), this.mergedFilter)
				: this.createQuickSearchFilter(fullTextStr)
			: this.mergedFilter ? luceneToString(this.mergedFilter) : '';
	}

	createQuickSearchAndFilterQuery(quicksearchQuery: string, filterQuery: LuceneExpression) {
		const globalNode: NodeExpression = {
			left: luceneToParse(quicksearchQuery),
			operator: 'AND',
			right: filterQuery,
			parenthesized: false
		};

		return luceneToString(globalNode);
	}

	createQuickSearchFilter(textSearch: string) {
		const escapedTextFilter = `*${ term.escape(textSearch) }*`;

		const customerReferenceField: FieldExpression = {
			field: 'customerReference',
			term: escapedTextFilter,
			quoted: false
		};

		const supplierIssuenumberField: FieldExpression = {
			field: 'supplierIssueNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const filterNode = {
			left: customerReferenceField,
			operator: 'OR',
			right: supplierIssuenumberField,
			parenthesized: true
		};

		return luceneToString(filterNode);
	}

	async goToFilter() {
		if (!this.currentFilters) {
			return;
		}
		this.listFilterService.filterToEdit = {
			filterState: this.currentFilters,
			datacenterType: this.currentFilters.datacenterType
		};
		await this.router.navigate(
			['filter'],
			{ queryParams: { type: this.datacenterType }, relativeTo: this.route }
		);
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<QualityIssue[]>) => void) {
		provideData(
			this.qualityIssueService.getIssues(
				ISSUE_FIELDS,
				this.filters,
				index,
				limit,
				[new OrderBy('modificationDate', OrderByDirection.DESC)],
				false
			).pipe(map(res => res.results))
		);
	}

	private getAppliedFilters() {
		const listFilter: ListFilter = {
			filterState: this.state.filter ?? [],
			datacenterType: this.datacenterType
		};
		const appliedFilter = this.state.filter
			? listFilter
			: this.listFilterService.getAppliedFilters(this.datacenterType) ?? listFilter;
		this.listFilterService.setCurrentFilter(appliedFilter);
		return appliedFilter;
	}
}
